import { create } from "zustand";
import { toast } from "react-toastify";

const useSessionStore = create((set, get) => ({
  user: null,
  token: null,
  isLoggedIn: false,
  checkingSession: true,
  loggingOut: false,

  updateUser: (newUser) => {
    console.log("Updating user in session store:", newUser);
    set({ user: newUser });
  },

  getToken: async () => {
    let tok = get().token;
    if (!tok) {
      tok = localStorage.getItem("token");
    }
    return tok;
  },

  checkSession: async () => {
    set({ checkingSession: true });
    let token = get().token || localStorage.getItem("token");
    if (!token) {
      set({
        user: null,
        token: null,
        isLoggedIn: false,
        checkingSession: false,
      });
      return;
    }
    try {
      const res = await fetch("/me", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      if (res.ok) {
        const userData = await res.json();
        set({
          user: userData,
          token,
          isLoggedIn: true,
        });
      } else {
        set({
          user: null,
          token: null,
          isLoggedIn: false,
        });
        localStorage.removeItem("token");
      }
    } catch (err) {
      console.error("checkSession error:", err);
      set({
        user: null,
        token: null,
        isLoggedIn: false,
      });
      localStorage.removeItem("token");
    } finally {
      set({ checkingSession: false });
    }
  },

  handleLogin: async (email, password) => {
    set({ submitting: true });
    try {
      const res = await fetch("/sign_in", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (res.ok && data.token) {
        localStorage.setItem("token", data.token);
        set({
          user: data.user,
          token: data.token,
          isLoggedIn: true,
        });
        toast("Logged in successfully!");
        return { success: true };
      } else {
        set({
          user: null,
          token: null,
          isLoggedIn: false,
        });
        toast(data.error || "Invalid email or password");
        return {
          success: false,
          error: data.error || "Invalid email or password",
        };
      }
    } catch (err) {
      console.error("Login error:", err);
      set({
        user: null,
        token: null,
        isLoggedIn: false,
      });
      toast("Login error: " + err.message);
      return { success: false, error: err.message };
    } finally {
      set({ submitting: false });
    }
  },

  handleSignUp: async (payload) => {
    set({ submitting: true });
    try {
      const res = await fetch("/sign_up", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (res.ok) {
        // No token or login after sign up
        toast(
          data.message ||
            "Registration successful. Please check your email to confirm your account before signing in."
        );
        return { success: true, message: data.message };
      } else {
        toast(data.errors ? data.errors.join(", ") : "Registration failed");
        return { success: false, errors: data.errors };
      }
    } catch (err) {
      console.error("Sign up error:", err);
      toast("Sign up error: " + err.message);
      return { success: false, error: err.message };
    } finally {
      set({ submitting: false });
    }
  },

  handleLogout: async (message = "Logged out successfully!") => {
    try {
      set({
        loggingOut: true,
      });
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No token found");
      const res = await fetch("/sign_out", {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      localStorage.removeItem("token");
      set({
        user: null,
        token: null,
        isLoggedIn: false,
      });
      toast(message);
    } catch (err) {
      console.error(err);
      toast("Logout error: " + err.message);
    }
  },
}));

export default useSessionStore;
